import { memo, ReactNode, useLayoutEffect, useMemo, useState } from 'react'

import { IContact } from 'clickNgo/entities/contact'
import { IPartner } from 'clickNgo/entities/partner/models'
import { PartnerGeneralInfoPanel } from 'clickNgo/entities/partner/ui/partnerGeneralInfoPanel'
import { formatErrorMessages } from 'clickNgo/helpers/api'
import { ConfirmDialog } from 'cng-common/components/confirmDialog'
import { InfoBlock, InfoBlockRounded, InfoBlockType } from 'cng-common/components/infoBlock'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { useGetFormDataField } from 'cng-common/features/form'
import { Wizard } from 'shared/components/wizard'

import { ContactCreatorFormKeys, CreateContactFormContent } from './configs'
import { useContactCreatorWizard } from './hooks'
import { WelcomeMessage } from './models'
import { contactCreatorTranslation } from './translation'

import styles from './ContactCreatorContent.module.scss'

type Props = WelcomeMessage & {
  partner: IPartner
  initialValues?: Partial<IContact>
  onClose: () => void
  onCreate: (contact: IContact) => Promise<void> | void
  onReactivate: (contact: IContact) => void
  isReplacementContact?: boolean
  source?: string
}

export const ContactCreatorContent = memo<Props>(
  ({
    partner,
    onClose,
    onCreate,
    initialValues,
    welcomeMessage,
    welcomeBtnText,
    welcomeTitle,
    onReactivate,
    isReplacementContact = false,
    source,
  }) => {
    const wizard = useContactCreatorWizard({
      initialValues,
      partner,
      onCreate,
      onClose,
      onReactivate,
      isReplacementContact,
      source,
    })
    const [isWelcomeMessageVisible, setIsWelcomeMessageVisible] = useState(
      Boolean(welcomeMessage) || Boolean(welcomeTitle)
    )
    const [errorMessages = []] = useGetFormDataField<string[]>(
      ContactCreatorFormKeys.Settings,
      CreateContactFormContent.fields.errorMessages.field
    )
    const [stepperSize, setStepperSize] = useState<'standard' | 'compact'>()
    const [hasStepper, setHasStepper] = useState(true)

    const formattedErrorMessages = useMemo(
      () => formatErrorMessages(errorMessages),
      [errorMessages]
    )

    const shared = useMemo(
      () => (
        <>
          {isReplacementContact ? null : <PartnerGeneralInfoPanel {...partner} />}
          <RenderWhen condition={errorMessages.length > 0}>
            {/* <div className={styles.errors}> */}
            <InfoBlock
              text={formattedErrorMessages}
              type={InfoBlockType.Danger}
              rounded={InfoBlockRounded.None}
            />
            {/* </div> */}
          </RenderWhen>
        </>
      ),
      [errorMessages.length, formattedErrorMessages, isReplacementContact, partner]
    )

    useLayoutEffect(() => {
      const resize = () => {
        setStepperSize(window.innerWidth > 1180 ? 'standard' : 'compact')
        setHasStepper(window.innerWidth > 1020)
      }

      resize()

      window.addEventListener('resize', resize)

      return () => {
        window.removeEventListener('resize', resize)
      }
    }, [])

    const classes = isReplacementContact ? { header: styles.header } : undefined

    return (
      <>
        <Wizard
          steps={wizard.steps}
          hasStepper={hasStepper}
          onCancel={onClose}
          stepperSize={stepperSize}
          shared={shared}
          classes={classes}
        />
        <ConfirmDialog
          isVisible={isWelcomeMessageVisible}
          onConfirm={() => setIsWelcomeMessageVisible(false)}
          title={welcomeTitle || contactCreatorTranslation.welcome.title}
          content={welcomeMessage}
          confirmBtnText={welcomeBtnText || contactCreatorTranslation.welcome.confirmBtn}
          isOnlyApply
        />
      </>
    )
  },
  (prev, next) =>
    prev.partner.partnerId === next.partner.partnerId &&
    prev.isReplacementContact === next.isReplacementContact &&
    prev.initialValues?.contactId === next.initialValues?.contactId &&
    prev.initialValues?.entryId379 === next.initialValues?.entryId379
)

ContactCreatorContent.displayName = 'ContactCreatorContent'
